import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Icon } from '@/design-system/components/icons/Icon';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody } from '@/design-system/components/typography';
import { SxProp, View, Image } from 'dripsy';
import _ from 'lodash';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { SetCardLimitStackParamList, SetCardLimitStackRoutes } from './type';
import { CardWithLimitProp } from '../../types';

export interface SetCardLimitProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  onDailyLimitType?: (type: string) => void;
  account: string;
  tab?: number;
  record?: CardWithLimitProp;
  func?: (cardId: string, atmLimit?: number, purchaseLimit?: number) => void;
  refresh?: () => void;
}

export const SetCardLimitWebZeroScreen: React.FC<SetCardLimitProps> = (props) => {
  const { onClose, onLayout, onDailyLimitType, account, tab, record, func, refresh } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const { topHeader, body, textMb, itemStyle, buttonBox } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<SetCardLimitStackParamList>>();

  const dailyLimitType = (type: string) => {
    onDailyLimitType && onDailyLimitType(type);
    navigation.replace(SetCardLimitStackRoutes.DailyPurchaseLimit, {
      onLayout: onLayout,
      dailyLimitType: type,
      account,
      tab,
      record,
      func,
      refresh
    });
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <View sx={topHeader}>
        <ModalHeader title={t('card-account-management::set-card-limit.title--set-card-limit')} closeModal={onClose} />
      </View>
      <View sx={body}>
        <TextBody sx={textMb} semiBold>
          {t('card-account-management::set-card-limit.text--select-limit-type')}
        </TextBody>
        <View sx={{ gap: 10 }}>
          <TextBody sx={itemStyle} onPress={() => dailyLimitType('PurchaseLimit')}>
            <TextBody semiBold>{t('card-account-management::set-card-limit.title--daily-purchase-limit')}</TextBody>
            <Icon name="arrowRightOrangeCircle" />
          </TextBody>
          <TextBody sx={itemStyle} onPress={() => dailyLimitType('ATMWithdrawalLimit')}>
            <TextBody semiBold>{t('card-account-management::set-card-limit.title--daily-withdraw-limit')}</TextBody>
            <Icon name="arrowRightOrangeCircle" />
          </TextBody>
          <TextBody sx={itemStyle} onPress={() => dailyLimitType('DailyLimit')}>
            <TextBody semiBold>{t('card-account-management::set-card-limit.title--daily-limit')}</TextBody>
            <Icon name="arrowRightOrangeCircle" />
          </TextBody>
          <TextBody sx={itemStyle} onPress={() => dailyLimitType('SingleTransactionLimit')}>
            <TextBody semiBold>{t('card-account-management::set-card-limit.title--single-transaction-limit')}</TextBody>
            <Icon name="arrowRightOrangeCircle" />
          </TextBody>
        </View>
        <View sx={buttonBox}>
          <UniButton
            variant={ButtonVariant.outline1}
            sx={{ width: '100%', height: 44 }}
            label={t('card-account-management::general.button--cancel')}
            onPress={onClose}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  topHeader: {
    height: 70,
    justifyContent: 'center'
  },
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  },
  itemStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    px: 20,
    py: 30,
    cursor: 'pointer',
    shadowOpacity: 0.1,
    shadowOffset: {
      width: 2,
      height: 2
    },
    shadowRadius: 10,
    borderRadius: 10
  },
  buttonBox: { flex: 1, display: 'grid', gap: '$3', mt: '$10' }
};
